import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import NavBar from '../components/NavBar';
import Footer from '../components/Footer';
import '../styles/HomePage.css';

const HomePage = () => {
  const auth = getAuth();
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsLoggedIn(!!user);
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [auth]);

  const handleCreateSession = () => {
    navigate('/sessions');
  };

  return (
    <div className="homepage">
      <NavBar />

      {/* Hero Banner Section */}
      <section className="hero">
        <div className="hero-content">
          <h1>Pocket Videoke – Online Karaoke Made for Real Parties</h1>
          <h2>Sing Together. Anytime. Anywhere.</h2>
          <p>Turn any screen into a karaoke machine. Pocket Videoke lets you create shared karaoke sessions, queue songs, and sing together — no bulky equipment, no complicated setup.</p>
          <p><strong>Perfect for:</strong> house parties, birthdays, team events, barkadas, and family gatherings.</p>
          <div className="hero-buttons">
            {isLoggedIn ? (
              <button onClick={handleCreateSession} className="btn-primary">👉 Start a session in seconds. No installation required.</button>
            ) : (
              <>
                <Link to="/register" className="btn-primary">👉 Start a session in seconds. No installation required.</Link>
                <Link to="/login" className="btn-secondary">Login</Link>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Why Pocket Videoke Section */}
      <section className="why-section">
        <h2>Why Pocket Videoke?</h2>
        <p className="section-intro">Most people already use YouTube for karaoke — but it breaks down fast during parties.</p>
        <p className="section-intro"><strong>Pocket Videoke fixes that.</strong></p>

        <div className="features-grid">
          <div className="feature-card">
            <h3>🎤 Built for Group Karaoke</h3>
            <ul>
              <li>Create a shared session anyone can join</li>
              <li>Guests add songs to the queue from their own phones</li>
              <li>No more passing the remote</li>
            </ul>
          </div>
          <div className="feature-card">
            <h3>📺 Works on Any Screen</h3>
            <ul>
              <li>Cast to Smart TVs, Chromecast, or Android TV</li>
              <li>One screen for lyrics, many phones for control</li>
            </ul>
          </div>
          <div className="feature-card">
            <h3>🆓 100% Free — Always</h3>
            <ul>
              <li>No subscriptions, no passes, no credit card</li>
              <li>Unlimited access at no cost</li>
              <li>Perfect for occasional and spontaneous karaoke</li>
            </ul>
          </div>
          <div className="feature-card">
            <h3>⚡ No App Download Needed</h3>
            <ul>
              <li>100% web-based</li>
              <li>Works on mobile, tablet, or desktop</li>
              <li>Just open a link and sing</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Millions of Songs Section */}
      <section className="songs-section">
        <h2>Millions of Karaoke Songs, Ready to Play</h2>
        <p>Pocket Videoke streams karaoke videos sourced from publicly available YouTube content.</p>
        <p>Search, queue, and play karaoke tracks instantly — all organized into a real-time karaoke experience designed for groups.</p>
        <p className="disclaimer"><em>Disclaimer: Pocket Videoke is not affiliated with, endorsed by, or sponsored by YouTube or Google LLC. YouTube is a trademark of Google LLC.</em></p>
      </section>

      {/* How It Works Section */}
      <section className="how-to-use">
        <h2>How It Works</h2>
        <div className="steps-container">
          <div className="step">
            <div className="step-number">1</div>
            <h3>Create a Session</h3>
            <p>Start a karaoke room in seconds</p>
          </div>
          <div className="step">
            <div className="step-number">2</div>
            <h3>Share the Link</h3>
            <p>Let friends join using their phones</p>
          </div>
          <div className="step">
            <div className="step-number">3</div>
            <h3>Add Songs</h3>
            <p>Everyone queues their favorite tracks</p>
          </div>
          <div className="step">
            <div className="step-number">4</div>
            <h3>Sing & Enjoy</h3>
            <p>Lyrics on TV, control on your phone</p>
          </div>
        </div>
        <p className="section-tagline">That's it. No setup headaches.</p>
      </section>

      {/* Free Access Section */}
      <section className="trial-section">
        <h2>100% Free. No Catches. No Credit Card.</h2>
        <p>Pocket Videoke is completely free for everyone — no subscriptions, no time limits, no hidden fees.</p>
        <p>Create a session and experience real group karaoke instantly. Perfect for spontaneous parties, family gatherings, or casual hangouts.</p>
        
        <h3>Why start now?</h3>
        <ul>
          <li>Create a karaoke session in seconds</li>
          <li>Invite friends via link or QR code</li>
          <li>Add songs collaboratively from any phone</li>
          <li>Cast lyrics to your TV or big screen</li>
          <li>No app downloads required</li>
        </ul>
        
        {isLoggedIn ? (
          <button onClick={handleCreateSession} className="btn-primary">👉 Start a session now — it's completely free!</button>
        ) : (
          <Link to="/register" className="btn-primary">👉 Create a free account and start singing today.</Link>
        )}
      </section>

      {/* Comparison Table Section */}
      <section className="comparison-section">
        <h2>Pocket Videoke vs Traditional Karaoke Apps</h2>
        <div className="comparison-table">
          <table>
            <thead>
              <tr>
                <th>Feature</th>
                <th>Pocket Videoke</th>
                <th>Traditional Karaoke Apps</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Works on TV</td>
                <td>✅ Yes</td>
                <td>❌ Often No</td>
              </tr>
              <tr>
                <td>Group Queue Control</td>
                <td>✅ Yes</td>
                <td>❌ Limited</td>
              </tr>
              <tr>
                <td>100% Free</td>
                <td>✅ Always Free</td>
                <td>❌ Paid / Monthly</td>
              </tr>
              <tr>
                <td>No App Install</td>
                <td>✅ Yes</td>
                <td>❌ Required</td>
              </tr>
              <tr>
                <td>Party-Friendly</td>
                <td>✅ Built for it</td>
                <td>❌ Solo-focused</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Perfect For Section */}
      <section className="perfect-for-section">
        <h2>Perfect For</h2>
        <ul className="perfect-for-list">
          <li>🎉 House parties</li>
          <li>🎂 Birthdays</li>
          <li>🏠 Family gatherings</li>
          <li>🏢 Team bonding</li>
          <li>🍻 Barkada nights</li>
        </ul>
        <p>Anywhere there's a screen and friends, Pocket Videoke fits.</p>
      </section>

      {/* Call to Action Section */}
      <section className="cta-section">
        <h2>Start Singing Today</h2>
        <p>Create your first karaoke session in minutes and turn any moment into a party.</p>
        <p className="cta-tagline">🎶 <strong>No equipment. No subscription. 100% free. Just karaoke.</strong></p>
        {isLoggedIn ? (
          <button onClick={handleCreateSession} className="btn-primary">👉 Start a session in seconds. No installation required.</button>
        ) : (
          <Link to="/register" className="btn-primary">👉 Start a session in seconds. No installation required.</Link>
        )}
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
